import React, { type ReactElement } from 'react'

// For React rendering - just use the children as-is
export interface SVGReactInfo {
  viewBox: string
  children: ReactElement[]
}

// For DOM creation - serialized format
export interface SVGDOMInfo {
  viewBox: string
  children: SVGChildData[]
}

export interface SVGChildData {
  tag: string
  attributes: Record<string, string | number>
  children?: SVGChildData[]
  textContent?: string
}

export const extractSVGForReact = (svgElement: SVGSVGElement | null): SVGReactInfo => {
  if (svgElement == null) {
    throw new Error('Failed to render SVG component')
  }

  const viewBox = svgElement.getAttribute('viewBox') ?? '0 0 24 24'

  // Convert DOM children to React elements
  const children: ReactElement[] = []
  for (const node of svgElement.childNodes) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element
      const props: Record<string, any> = {}

      for (const attr of element.attributes) {
        // Convert kebab-case to camelCase for React props
        const propName = attr.name.replaceAll(/-([a-z])/g, (_, letter) => letter.toUpperCase())
        props[propName] = attr.value
      }

      children.push(React.createElement(element.tagName.toLowerCase(), props))
    }
  }

  return { viewBox, children }
}

export const extractSVGForDOM = (svgElement: SVGSVGElement | null): SVGDOMInfo => {
  if (svgElement == null) {
    throw new Error('Failed to render SVG component')
  }

  // Serialize the actual DOM element
  const serializeElement = (element: Element): SVGChildData => {
    const attributes: Record<string, string | number> = {}
    for (const attr of element.attributes) {
      attributes[attr.name] = attr.value
    }

    const result: SVGChildData = {
      tag: element.tagName.toLowerCase(),
      attributes
    }

    const children: SVGChildData[] = []
    for (const child of element.childNodes) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        children.push(serializeElement(child as Element))
      } else if (child.nodeType === Node.TEXT_NODE && child.textContent != null && child.textContent.trim() !== '') {
        result.textContent = child.textContent
      }
    }

    if (children.length > 0) {
      result.children = children
    }

    return result
  }

  const children: SVGChildData[] = []
  svgElement.childNodes.forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      children.push(serializeElement(child as Element))
    }
  })

  const viewBox = svgElement.getAttribute('viewBox') ?? '0 0 24 24'

  return { viewBox, children }
}

// Helper to create DOM SVG from serialized data
export const createSVGElement = (data: SVGChildData): SVGElement => {
  const element = document.createElementNS('http://www.w3.org/2000/svg', data.tag)

  // Set attributes
  Object.entries(data.attributes).forEach(([key, value]) => {
    element.setAttribute(key, String(value))
  })

  // Set text content
  if (data.textContent != null) {
    element.textContent = data.textContent
  }

  // Append children recursively
  if (data.children != null) {
    for (const child of data.children) {
      element.appendChild(createSVGElement(child))
    }
  }

  return element
}
